import {
  addDays,
  daysBetween,
  localDateTimeOf,
  readImportantDate,
  type ImportantDateFields,
  type LiveImportantDate,
} from '@wiztime/core';
import type { CalendarApi } from './calendarModule';

/** Pure logic of the important dates UI: nothing here reads a clock or touches the DOM. */

/**
 * The date on the device's calendar at `nowMs` (`YYYY-MM-DD`). Countdowns and reminders of
 * important dates use this, not the personal day: at 01:00 with a day start of 06:00 the
 * personal day is still yesterday, but a deadline dated today is "today" (docs/time-drift.md D5, D8).
 */
export function calendarDateOf(nowMs: number): string {
  return localDateTimeOf(nowMs).slice(0, 10);
}

/** An important date that this build understands and that is active. */
export interface ReadableDate {
  readonly id: string;
  readonly record: LiveImportantDate;
  readonly fields: ImportantDateFields;
}

/** The records this build can interpret, in input order; `activeOnly` leaves out deactivated ones. */
export function readableDates(records: readonly LiveImportantDate[], activeOnly: boolean): ReadableDate[] {
  return records.flatMap((record) => {
    const fields = readImportantDate(record.data);
    return fields === null || (activeOnly && !fields.active) ? [] : [{ id: record.id, record, fields }];
  });
}

export interface DateEntry {
  readonly kind: 'date';
  readonly id: string;
  readonly fields: ImportantDateFields;
  /** Next occurrence on or after today, `YYYY-MM-DD`. */
  readonly date: string;
  readonly daysUntil: number;
  /** `daysUntil` when today is one of the lead days (the day to notify), else null. */
  readonly leadDay: number | null;
}

export interface HolidayEntry {
  readonly kind: 'holiday';
  readonly id: string;
  readonly packId: string;
  readonly holidayId: string;
  readonly date: string;
  readonly daysUntil: number;
}

export type UpcomingEntry = DateEntry | HolidayEntry;

export interface HolidaySettings {
  readonly holidayPacks: readonly string[];
  readonly holidayWindowDays: number;
}

/**
 * The entries of the "upcoming" card on `today` (a calendar date of the device): active
 * important dates inside their largest lead day, and holidays of the enabled packs inside the
 * holiday window. Ordered by days until, important dates before holidays on the same day.
 */
export function upcomingEntries(
  calendar: CalendarApi,
  dates: readonly ReadableDate[],
  holidays: HolidaySettings,
  today: string,
): UpcomingEntry[] {
  const active = dates.filter((entry) => entry.fields.active);
  const reminders = calendar.upcomingReminders(
    active.map((entry) => ({ ...entry.fields, id: entry.id })),
    today,
  );
  const entries: UpcomingEntry[] = [];
  for (const reminder of reminders) {
    if (!reminder.withinWindow) continue;
    const source = active.find((entry) => entry.id === reminder.item.id);
    if (source === undefined) continue;
    entries.push({
      kind: 'date',
      id: source.id,
      fields: source.fields,
      date: reminder.nextDate,
      daysUntil: reminder.daysUntil,
      leadDay: reminder.leadDay,
    });
  }
  if (holidays.holidayPacks.length > 0) {
    const found = calendar.holidaysBetween(
      holidays.holidayPacks,
      today,
      addDays(today, holidays.holidayWindowDays),
    );
    for (const holiday of found) {
      entries.push({
        kind: 'holiday',
        id: `${holiday.packId}:${holiday.holidayId}:${holiday.date}`,
        packId: holiday.packId,
        holidayId: holiday.holidayId,
        date: holiday.date,
        daysUntil: daysBetween(today, holiday.date),
      });
    }
  }
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort(
      (a, b) =>
        a.entry.daysUntil - b.entry.daysUntil ||
        (a.entry.kind === b.entry.kind ? 0 : a.entry.kind === 'date' ? -1 : 1) ||
        a.index - b.index,
    )
    .map(({ entry }) => entry);
}

/** Next occurrence of one important date for the settings list; null when it has none. */
export function nextOf(
  calendar: CalendarApi,
  fields: ImportantDateFields,
  today: string,
): { readonly date: string; readonly daysUntil: number } | null {
  const date = calendar.nextOccurrence(fields, today);
  return date === null ? null : { date, daysUntil: daysBetween(today, date) };
}

/** Order of the settings list: next occurrence ascending; deactivated and finished dates last. */
export function sortByNext<T extends { readonly fields: ImportantDateFields }>(
  calendar: CalendarApi,
  items: readonly T[],
  today: string,
): Array<T & { readonly next: { readonly date: string; readonly daysUntil: number } | null }> {
  const withNext = items.map((item) => ({ ...item, next: nextOf(calendar, item.fields, today) }));
  const rank = (item: (typeof withNext)[number]): number =>
    !item.fields.active ? 2 : item.next === null ? 1 : 0;
  return withNext
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        rank(a.item) - rank(b.item) ||
        (a.item.next?.date ?? '').localeCompare(b.item.next?.date ?? '') ||
        a.index - b.index,
    )
    .map(({ item }) => item);
}

/** Which countdown wording applies. */
export const Countdown = { Today: 'today', Tomorrow: 'tomorrow', Days: 'days' } as const;
export type Countdown = (typeof Countdown)[keyof typeof Countdown];

export function countdownOf(daysUntil: number): Countdown {
  return daysUntil <= 0 ? Countdown.Today : daysUntil === 1 ? Countdown.Tomorrow : Countdown.Days;
}

/** Device-local record of the system notifications already sent: item id -> calendar date of the notification. */
export interface NotifiedState {
  readonly notified: Readonly<Record<string, string>>;
}

export const EMPTY_NOTIFIED_STATE: NotifiedState = { notified: {} };

const DATE_TEXT = /^\d{4}-\d{2}-\d{2}$/;

/** The state in stored JSON; anything unreadable is the empty state. */
export function readNotifiedState(value: unknown): NotifiedState {
  if (typeof value !== 'object' || value === null) return EMPTY_NOTIFIED_STATE;
  const raw = (value as { notified?: unknown }).notified;
  if (typeof raw !== 'object' || raw === null) return EMPTY_NOTIFIED_STATE;
  const notified: Record<string, string> = {};
  for (const [id, date] of Object.entries(raw)) {
    if (typeof date === 'string' && DATE_TEXT.test(date)) notified[id] = date;
  }
  return { notified };
}

/** The dates to notify about now: today is one of their lead days and they were not notified today. */
export function notificationsDue(
  entries: readonly UpcomingEntry[],
  state: NotifiedState,
  today: string,
): DateEntry[] {
  return entries.flatMap((entry) =>
    entry.kind === 'date' && entry.leadDay !== null && state.notified[entry.id] !== today ? [entry] : [],
  );
}

/** The state after notifying `ids` on `today`; entries of earlier days are dropped. */
export function markNotified(state: NotifiedState, ids: readonly string[], today: string): NotifiedState {
  const notified: Record<string, string> = {};
  for (const [id, date] of Object.entries(state.notified)) {
    if (date >= today) notified[id] = date;
  }
  for (const id of ids) notified[id] = today;
  return { notified };
}
